import cx from 'classnames';
import {useMediaQuery} from 'hooks/useMediaQuery';
import {BreakpointName, breakpoints} from 'lib/breakpoints';
import React, {AllHTMLAttributes, forwardRef, memo, useMemo} from 'react';
import {FCC} from 'types/react';

import cn from './Text.module.css';

type TextOwnCommonProps = {
    className?: string;
    children?: React.ReactNode;
    as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
};

export type TextCommonProps = TextOwnCommonProps &
    JSX.IntrinsicAttributes &
    AllHTMLAttributes<any>;

function getTextClasses(className: string, props: TextOwnCommonProps) {
    return cx(className, props.className, cn.defaultColor, cn.breakWord);
}

// Конструктор компонентов
function createComponent(className: string, name: string) {
    // Позволяет рендерить любой элемент с указанными стилями
    const TextComponent = memo(
        forwardRef<HTMLElement, TextCommonProps>((props, ref?) => {
            const {
                as = 'div',
                children,
                className: propsClassName,
                ...restProps
            } = props;
            const Component = as;

            return (
                <Component
                    ref={ref}
                    className={getTextClasses(className, {
                        className: propsClassName,
                    })}
                    {...restProps}
                >
                    {children}
                </Component>
            );
        }),
    );

    TextComponent.displayName = name;

    return TextComponent;
}

const Bold12 = createComponent(cn.bold12, 'Bold12');
const Bold14 = createComponent(cn.bold14, 'Bold14');
const Bold16 = createComponent(cn.bold16, 'Bold16');
const Bold18 = createComponent(cn.bold18, 'Bold18');
const Bold20 = createComponent(cn.bold20, 'Bold20');
const Bold24 = createComponent(cn.bold24, 'Bold24');
const Bold30 = createComponent(cn.bold30, 'Bold30');

const Semibold12 = createComponent(cn.semibold12, 'Semibold12');
const Semibold14 = createComponent(cn.semibold14, 'Semibold14');
const Semibold16 = createComponent(cn.semibold16, 'Semibold16');

const Regular12 = createComponent(cn.regular12, 'Regular12');
const Regular14 = createComponent(cn.regular14, 'Regular14');
const Regular16 = createComponent(cn.regular16, 'Regular16');

const textComponentsList = {
    Bold12,
    Bold14,
    Bold16,
    Bold18,
    Bold20,
    Bold24,
    Bold30,

    Semibold12,
    Semibold14,
    Semibold16,

    Regular12,
    Regular14,
    Regular16,
};
type TextComponentNames = keyof typeof textComponentsList;
type ResponsiveProps = {
    breakpointsMap: Partial<Record<BreakpointName, TextComponentNames>> & {
        default: TextComponentNames;
    };
};

const Responsive: FCC<ResponsiveProps & TextCommonProps> = ({
    breakpointsMap,
    ...restProps
}) => {
    const greaterThanSm = useMediaQuery(breakpoints.up('sm'));
    const greaterThanMd = useMediaQuery(breakpoints.up('md'));
    const greaterThanLg = useMediaQuery(breakpoints.up('lg'));

    const TextComponent = useMemo(() => {
        if (greaterThanLg && breakpointsMap.lg) {
            return textComponentsList[breakpointsMap.lg];
        }

        if (greaterThanMd && breakpointsMap.md) {
            return textComponentsList[breakpointsMap.md];
        }

        if (greaterThanSm && breakpointsMap.sm) {
            return textComponentsList[breakpointsMap.sm];
        }

        return textComponentsList[breakpointsMap.default];
    }, [greaterThanLg, greaterThanSm, greaterThanMd, breakpointsMap]);

    return <TextComponent {...restProps} />;
};

export const Text = {
    ...textComponentsList,

    Responsive,
};
